import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { 
  X, 
  FloppyDisk,
  Info,
  Tag,
  GitBranch,
  ArrowRight,
  Plus,
  Trash,
  Gear,
  FileText,
  ArrowCircleRight,
  ChatCircle,
  Database,
  Wrench,
  Link,
  Eye,
  Clock,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/text-area';
import { Input } from '../ui/input';
import { ConfirmModal } from '../ui/confirm-modal';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import BrainItemSelector from './BrainItemSelector';
import { ConditionsList } from './ConditionEditor';
import WorkflowFlow from './WorkflowFlow';
import StepEditPanel from './StepEditPanel';
import BrainActionModal from '../../brain/BrainActionModal';
import BrainSettingConditions from '../brain/BrainSettingConditions';
import { getUserAccountId } from '../../utils/accountUtils';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';

// Animation variants
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { delay: 0.1, duration: 0.2 } }
};

const modalVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.98 },
  visible: { 
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] }
  },
  exit: { 
    opacity: 0, y: 20, scale: 0.98,
    transition: { duration: 0.2, ease: "easeOut" }
  }
};

// Step types
const STEP_TYPES = [
  { 
    value: 'start', 
    label: 'Start',
    icon: <ArrowCircleRight className="h-4 w-4 text-green-500" />,
    description: 'Beginning of the workflow'
  },
  { 
    value: 'action', 
    label: 'Action',
    icon: <Gear className="h-4 w-4 text-blue-500" />,
    description: 'Execute brain items or actions'
  },
  { 
    value: 'condition', 
    label: 'Condition',
    icon: <GitBranch className="h-4 w-4 text-orange-500" />,
    description: 'Decision point based on conditions'
  },
  { 
    value: 'end', 
    label: 'End',
    icon: <FileText className="h-4 w-4 text-red-500" />,
    description: 'End of the workflow'
  }
];

// Generate default workflow steps with proper UUIDs
const generateDefaultSteps = () => {
  const startId = crypto.randomUUID();

  return [
    { 
      id: startId, 
      name: 'Start Workflow', 
      type: 'start', 
      order: 1, 
      config: {},
      parentId: null,
      references: [],
      conditions: []
    }
  ];
};

const WorkflowModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  workflow = null, 
  isNew = true,
  isPreLoaded = false,
  preSelectedCategoryId = null,
  categories = [],
  isInline = false,
  onUpdateSetting, // Add new prop
  onCreateCategory, // Add new prop for category creation
  newlyCreatedCategory, // Add new prop for auto-selecting newly created category
  onClearNewlyCreatedCategory, // Add new prop for clearing newly created category
  onOpenActivationConditions,
  setting
}) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);

  const initializedRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Brain item selector state
  const [isBrainItemSelectorOpen, setIsBrainItemSelectorOpen] = useState(false);
  const [currentStepForReferences, setCurrentStepForReferences] = useState(null);

  // NEW: Brain Action Modal state for creating new items from workflow
  const [isBrainActionModalOpen, setIsBrainActionModalOpen] = useState(false);

  // SIMPLIFIED: Step edit panel state - single source of truth
  const [editingStepId, setEditingStepId] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [accountId, setAccountId] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    description: '',
    triggers: [],
    category_id: preSelectedCategoryId || '',
    steps: generateDefaultSteps(),
    trigger_phrases: [],
    execution_timeout_seconds: 300,
    max_execution_depth: 50,
    is_active: true
  });

  // Trigger input state
  const [triggerInput, setTriggerInput] = useState('');

  // Initialize form data when modal opens or workflow changes
  useEffect(() => {
    if (isOpen) {
      if (workflow && !isNew) {
        // Edit existing workflow - always reinitialize when workflow changes

        const loadRequirements = async () => {
          if (workflow.id) {
            const { data, error } = await supabase
              .from('brain_setting_requirements')
              .select('*, brain_conversation_topics(topic_name)')
              .eq('settings_id', workflow.id);

            if (error) {
              console.error("Error fetching requirements for workflow", error);
            } else {
              setRequirements(data || []);
            }
          }
        };
        loadRequirements();

        // Extract steps from various possible locations
        let workflowSteps = workflow.fullData?.steps || workflow.workflow_config?.steps || workflow.steps;

        // If no valid steps found or empty array, generate defaults
        if (!workflowSteps || !Array.isArray(workflowSteps) || workflowSteps.length === 0) {
          workflowSteps = generateDefaultSteps();
        }

        setFormData({
          id: workflow.id,
          name: workflow.name || '',
          description: workflow.description || '',
          triggers: workflow.triggers || [],
          category_id: workflow.category_id || preSelectedCategoryId || '',
          steps: workflowSteps,
          trigger_phrases: workflow.trigger_phrases || [],
          execution_timeout_seconds: workflow.execution_timeout_seconds || 300,
          max_execution_depth: workflow.max_execution_depth || 50,
          is_active: workflow.is_active ?? true
        });
      } else {
        // New workflow
        setFormData({
          id: null,
          name: '',
          description: '',
          triggers: [],
          category_id: preSelectedCategoryId || '',
          steps: generateDefaultSteps(),
          trigger_phrases: [],
          execution_timeout_seconds: 300,
          max_execution_depth: 50,
          is_active: true
        });
      }
      initializedRef.current = true;
    }
  }, [isOpen, workflow?.id, isNew, preSelectedCategoryId]);

  useEffect(() => {
    const fetchAccountId = async () => {
      if (isOpen) {
        const { accountId: id } = await getUserAccountId();
        setAccountId(id);
      }
    };
    fetchAccountId();
  }, [isOpen]);

  // Auto-select newly created category
  useEffect(() => {
    if (newlyCreatedCategory && isOpen) {
      // Select the new category
      setFormData(prev => ({
        ...prev,
        category_id: newlyCreatedCategory.id
      }));

      setHasUnsavedChanges(true);

      // Clear the newly created category after using it
      if (onClearNewlyCreatedCategory) {
        onClearNewlyCreatedCategory();
      }
    }
  }, [newlyCreatedCategory, isOpen]);

  // Track unsaved changes
  useEffect(() => {
    if (initializedRef.current) {
      setHasUnsavedChanges(true);
    }
  }, [formData]);

  // Handle category changes including new category creation
  const handleCategoryChange = (value) => {
    if (value === 'new') {
      if (onCreateCategory) {
        onCreateCategory();
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      category_id: value
    }));
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle trigger addition
  const handleAddTrigger = () => {
    if (triggerInput.trim()) {
      setFormData(prev => ({
        ...prev,
        triggers: [...prev.triggers, triggerInput.trim()]
      }));
      setTriggerInput('');
    }
  };

  // Handle trigger removal
  const handleRemoveTrigger = (index) => {
    setFormData(prev => ({
      ...prev,
      triggers: prev.triggers.filter((_, i) => i !== index)
    }));
  };

  // Handle step addition
  const handleAddStep = (stepType = 'action', parentId = null) => {
    // Prevent adding start steps - there should always be exactly one
    if (stepType === 'start') {
      toast.error("Cannot add additional start steps. Each workflow has exactly one start step.");
      return null;
    }

    const newStepId = crypto.randomUUID();
    const newStep = {
      id: newStepId,
      name: `New ${stepType.charAt(0).toUpperCase() + stepType.slice(1)} Step`,
      type: stepType,
      order: null, // No automatic ordering
      config: {},
      parentId: parentId, // Set the parent relationship
      references: [],
      conditions: []
    };

    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, newStep]
    }));

    setHasUnsavedChanges(true);
    return newStepId;
  };

  // Handle step removal
  const handleRemoveStep = (stepId) => {
    setFormData(prev => {
      const stepToRemove = prev.steps.find(step => step.id === stepId);

      // Completely prevent removing start steps
      if (stepToRemove?.type === 'start') {
        toast.error("Cannot delete the start step. Every workflow must have exactly one start step.");
        return prev;
      }

      // Prevent removing if it would leave workflow with less than 2 steps
      if (prev.steps.length <= 2) {
        toast.error("Workflow must have at least 2 steps");
        return prev;
      }

      // Warn if removing the only end step
      const endSteps = prev.steps.filter(step => step.type === 'end');
      if (stepToRemove?.type === 'end' && endSteps.length === 1) {
        toast.error("Cannot remove the only end step in the workflow");
        return prev;
      }

      return {
        ...prev,
        steps: prev.steps.filter(step => step.id !== stepId)
      };
    });

    // Close edit panel if we're deleting the currently edited step
    if (editingStepId === stepId) {
      setEditingStepId(null);
    }
  };

  // Handle step duplication
  const handleDuplicateStep = (originalStep) => {
    // Prevent duplicating start steps - there should always be exactly one
    if (originalStep.type === 'start') {
      toast.error("Cannot duplicate the start step. Each workflow has exactly one start step.");
      return;
    }

    setFormData(prev => {
      // Find the highest order among existing steps
      const maxOrder = prev.steps.reduce((max, step) => Math.max(max, step.order || 0), 0);

      // Find any end steps
      const endSteps = prev.steps.filter(step => step.type === 'end');

      let newOrder;
      let updatedSteps = [...prev.steps];

      if (originalStep.type === 'end') {
        // End steps should always be last
        newOrder = maxOrder + 1;
      } else if (endSteps.length > 0) {
        // Insert duplicate step before the first end step
        const firstEndOrder = Math.min(...endSteps.map(step => step.order || 0));
        newOrder = firstEndOrder;

        // Increment the order of all end steps to make room
        updatedSteps = prev.steps.map(step => {
          if (step.type === 'end' && (step.order || 0) >= firstEndOrder) {
            return { ...step, order: (step.order || 0) + 1 };
          }
          return step;
        });
      } else {
        // No end steps, just add at the end
        newOrder = maxOrder + 1;
      }

      // Create duplicate step with new ID and name
      const duplicatedStep = {
        ...originalStep,
        id: crypto.randomUUID(),
        name: `${originalStep.name} (Copy)`,
        order: newOrder,
        // Deep copy references and conditions to avoid shared state
        references: originalStep.references ? [...originalStep.references.map(ref => ({
          ...ref,
          id: crypto.randomUUID()
        }))] : [],
        conditions: originalStep.conditions ? [...originalStep.conditions.map(cond => ({
          ...cond,
          id: crypto.randomUUID()
        }))] : []
      };

      // Add the duplicated step to the updated steps array
      return {
        ...prev,
        steps: [...updatedSteps, duplicatedStep]
      };
    });
  };

  // Handle step update
  const handleUpdateStep = (stepId, updates) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map(step => 
        step.id === stepId ? { ...step, ...updates } : step
      )
    }));
  };

  // SIMPLIFIED: Handle step edit from React Flow
  const handleStepEdit = useCallback((step) => {
    setEditingStepId(step.id);
  }, [formData.steps]);

  // Get the currently editing step
  const currentEditingStep = editingStepId ? 
    formData.steps.find(step => step.id === editingStepId) : null;

  // Handle step update from edit panel
  const handleStepUpdateFromPanel = (updatedStep) => {
    handleUpdateStep(updatedStep.id, updatedStep);
  };

  // Handle adding brain item reference to a step
  const handleAddBrainItemReference = (stepId) => {
    setCurrentStepForReferences(stepId);
    setIsBrainItemSelectorOpen(true);
  };

  // Handle brain item selection
  const handleBrainItemSelect = (brainItem) => {
    if (!currentStepForReferences) return;

    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map(step => {
        if (step.id === currentStepForReferences) {
          const newReference = {
            id: crypto.randomUUID(),
            ...brainItem,
            execution_order: (step.references || []).length + 1
          };
          const updatedStep = {
            ...step,
            references: [...(step.references || []), newReference]
          };

          // Auto-name the step if it's an action step with exactly one reference
          if (step.type === 'action' && updatedStep.references.length === 1) {
            updatedStep.name = brainItem.name;
          }

          return updatedStep;
        }
        return step;
      })
    }));

    setIsBrainItemSelectorOpen(false);
    setCurrentStepForReferences(null);
  };

  // NEW: Handle creating new brain items from workflow
  const handleCreateNewBrainItem = () => {
    setIsBrainItemSelectorOpen(false); // Close brain item selector
    setIsBrainActionModalOpen(true); // Open brain action modal
  };

  // NEW: Handle brain action selection (similar to main Brain.js)
  const handleBrainActionSelect = (actionType) => {
    setIsBrainActionModalOpen(false);

    // Steve Jobs approach: Keep it simple and elegant
    // Instead of complex modal stacking, emit a custom event that the main Brain.js can handle
    // This keeps the workflow editor focused while enabling the creation flow
    const event = new CustomEvent('selectBrainAction', {
      detail: { actionType }
    });
    window.dispatchEvent(event);

    // Show helpful message to user
    toast.success(
      `Opening ${actionType.replace('_', ' ')} creator. Once you create it, return here and refresh to select it.`,
      { duration: 4000 }
    );
  };

  // Handle removing brain item reference
  const handleRemoveBrainItemReference = (stepId, referenceId) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map(step => {
        if (step.id === stepId) {
          const updatedStep = {
            ...step,
            references: (step.references || []).filter(ref => ref.id !== referenceId)
          };

          // Reset auto-name for action steps when no references
          if (step.type === 'action' && updatedStep.references.length === 0) {
            updatedStep.name = '';
          }

          return updatedStep;
        }
        return step;
      })
    }));
  };

  // Handle adding condition to a step
  const handleAddCondition = (stepId) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map(step => {
        if (step.id === stepId) {
          const conditionCount = (step.conditions || []).length;
          const defaultName = `Condition ${conditionCount + 1}`;

          const newCondition = {
            id: crypto.randomUUID(),
            condition_name: defaultName, // Generate meaningful default name
            condition_type: 'equals',
            variable_path: 'user_input',
            condition_operator: 'equals',
            expected_value: '',
            target_step_id: '',
            condition_order: conditionCount + 1,
            is_active: true
          };
          return {
            ...step,
            conditions: [...(step.conditions || []), newCondition]
          };
        }
        return step;
      })
    }));
  };

  // Handle updating condition
  const handleUpdateCondition = (stepId, conditionIndex, updatedCondition) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map(step => {
        if (step.id === stepId) {
          const conditions = [...(step.conditions || [])];
          conditions[conditionIndex] = updatedCondition;
          return { ...step, conditions };
        }
        return step;
      })
    }));
  };

  // Handle deleting condition
  const handleDeleteCondition = (stepId, conditionIndex) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map(step => {
        if (step.id === stepId) {
          const conditions = [...(step.conditions || [])];
          conditions.splice(conditionIndex, 1);
          return { ...step, conditions };
        }
        return step;
      })
    }));
  };

  // Validate form
  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error("Please enter a workflow name");
      return false;
    }

    if (!formData.category_id) {
      toast.error("Please select a category");
      return false;
    }

    if (formData.steps.length < 2) {
      toast.error("A workflow must have at least 2 steps");
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if this is a real database ID or a temporary ID
      const isRealId = formData.id && !formData.id.toString().startsWith('add-') && !formData.id.toString().startsWith('edit-') && !formData.id.toString().startsWith('new-');

      const saveData = {
        id: isRealId ? formData.id : null,
        name: formData.name.trim(),
        description: formData.description.trim(),
        triggers: formData.triggers,
        category_id: formData.category_id,
        type: 'workflow',
        workflow_config: {
          steps: formData.steps,
          trigger_phrases: formData.trigger_phrases,
          execution_timeout_seconds: formData.execution_timeout_seconds,
          max_execution_depth: formData.max_execution_depth,
          is_active: formData.is_active
        }
      };

      const saveResult = await onSave(saveData);

      // After successful save, update requirements
      if (saveResult && saveResult.success && saveResult.id) {
        const settingsId = saveResult.id;
        const { error: deleteError } = await supabase
          .from('brain_setting_requirements')
          .delete()
          .eq('settings_id', settingsId);

        if (deleteError) throw deleteError;

        if (requirements.length > 0) {
          const requirementsToInsert = requirements.map(req => ({
            settings_id: settingsId,
            topic_id: req.topic_id,
            required_answer: req.required_answer
          }));
          const { error: insertError } = await supabase
            .from('brain_setting_requirements')
            .insert(requirementsToInsert);
          if (insertError) throw insertError;
        }
      }

      // Always notify the parent of the result.
      // The parent component is responsible for handling UI updates and closing logic.
      if (onClose) {
        onClose(saveResult);
      }

      if (!isInline) {
        // For non-inline (popup) mode, show a toast message and reset state,
        // as the component will be unmounted by the parent.
        toast.success(isNew ? "Workflow created successfully" : "Workflow updated successfully");
        initializedRef.current = false;
        setHasUnsavedChanges(false);
      }
      // For inline mode, the parent (BrainMainContent) handles everything.

    } catch (error) {
      console.error('Error saving workflow:', error);
      toast.error("Failed to save workflow");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close with unsaved changes check
  const handleCloseAttempt = () => {
    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      handleCloseConfirmed();
    }
  };

  // Confirmed close handling
  const handleCloseConfirmed = () => {
    initializedRef.current = false;
    setIsUnsavedChangesModalOpen(false);
    setHasUnsavedChanges(false);
    setEditingStepId(null);
    onClose();
  };

  // Get step type configuration
  const getStepTypeConfig = (type) => {
    return STEP_TYPES.find(stepType => stepType.value === type) || STEP_TYPES[1];
  };

  // If not open, don't render
  if (!isOpen) return null;

  // Render BrainItemSelector and BrainActionModal at top level regardless of inline/modal mode
  const topLevelModals = (
    <>
      {/* Brain Item Selector */}
      <BrainItemSelector
        isOpen={isBrainItemSelectorOpen}
        onClose={() => {
          setIsBrainItemSelectorOpen(false);
          setCurrentStepForReferences(null);
        }}
        onSelect={handleBrainItemSelect}
        onCreateNew={handleCreateNewBrainItem}
        categories={categories}
        selectedItems={currentStepForReferences ? 
          formData.steps.find(s => s.id === currentStepForReferences)?.references || [] : 
          []
        }
      />

      {/* Brain Action Modal */}
      <BrainActionModal
        isOpen={isBrainActionModalOpen}
        onClose={() => setIsBrainActionModalOpen(false)}
        onActionSelect={handleBrainActionSelect}
      />
    </>
  );

  // Inline rendering for BrainMainContent
  if (isInline) {
    return (
      <>
        {topLevelModals}
        <div className="h-full bg-white overflow-hidden flex flex-col">
          {/* Elegant Header with Gradient */}
          <motion.div 
            className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200/50 flex justify-between items-center bg-white/70 backdrop-blur-sm z-20 flex-shrink-0"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.3 }}
          >
            <div className="flex items-center">
              <motion.div 
                className="w-10 h-10 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 shadow-inner flex items-center justify-center mr-3 flex-shrink-0"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
              >
                <GitBranch className="h-5 w-5 text-white" />
              </motion.div>
              <h2 className="text-lg sm:text-xl font-light text-gray-900 tracking-tight">
                {isNew ? 'Create Workflow' : 'Edit Workflow'}
              </h2>
            </div>

            <div className="flex items-center space-x-2">
              {onOpenActivationConditions && setting && (
                 <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onOpenActivationConditions(setting)}
                    className="text-gray-700 border-gray-200 hover:bg-gray-50 rounded-xl"
                 >
                   <Clock className="w-4 h-4 mr-2" />
                   Conditions
                 </Button>
              )}
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                  size="sm"
                  className="bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white min-w-[100px] shadow-inner rounded-xl"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-1">
                      <svg className="animate-spin h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-xs sm:text-sm font-light">Saving</span>
                    </span>
                  ) : (
                    <span className="text-xs sm:text-sm font-light">Save</span>
                  )}
                </Button>
              </motion.div>
            </div>
          </motion.div>

          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Basic Settings */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700 flex items-center">
                  <Info className="h-4 w-4 text-gray-500 mr-1.5" />
                  Basic Information
                </h3>

                {/* Name */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-700">
                    Workflow Name <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter workflow name"
                    className="text-sm"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-700">
                    Description
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Describe what this workflow does"
                    className="text-sm"
                    rows={3}
                  />
                </div>

                {/* Category Selection */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-700">
                    Category <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <Select
                    value={formData.category_id}
                    onValueChange={handleCategoryChange}
                  >
                    <SelectTrigger className="h-10 text-sm border-gray-200">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent className="z-[10005]">
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id} className="text-sm">
                          {category.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="new" className="text-sm text-gray-500">
                        <Plus className="h-4 w-4 mr-1" /> New Category
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Qualifiers Section */}
              {accountId && (
                <div className="space-y-4 pt-4 border-t border-gray-100">
                   <BrainSettingConditions
                    settingId={formData.id}
                    accountId={accountId}
                    initialRequirements={requirements}
                    onRequirementsChange={(newReqs) => {
                      setRequirements(newReqs);
                      setHasUnsavedChanges(true);
                    }}
                  />
                </div>
              )}

              {/* Triggers */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700 flex items-center">
                  <Tag className="h-4 w-4 text-gray-500 mr-1.5" />
                  Trigger Phrases
                </h3>

                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      value={triggerInput}
                      onChange={(e) => setTriggerInput(e.target.value)}
                      placeholder="Add a trigger phrase"
                      className="text-sm"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddTrigger()}
                    />
                    <Button
                      type="button"
                      onClick={handleAddTrigger}
                      className="px-3 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {formData.triggers.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.triggers.map((trigger, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                        >
                          {trigger}
                          <button
                            type="button"
                            onClick={() => handleRemoveTrigger(index)}
                            className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-green-600 hover:bg-green-200 focus:outline-none"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Workflow Steps - React Flow */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700 flex items-center">
                    <GitBranch className="h-4 w-4 text-gray-500 mr-1.5" />
                    Workflow Design
                  </h3>
                  <div className="flex items-center space-x-4">
                    <div className="text-xs text-gray-500">
                      Click on steps to edit • {formData.steps.length} step{formData.steps.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>

                <div className="w-full h-96 border border-gray-200 rounded-lg overflow-hidden">
                  <WorkflowFlow
                    steps={formData.steps || []}
                    onStepEdit={handleStepEdit}
                    onAddStep={handleAddStep}
                    onDeleteStep={handleRemoveStep}
                    className="w-full h-full"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Step Edit Panel */}
          {editingStepId && currentEditingStep && (
            <StepEditPanel
              step={currentEditingStep}
              isOpen={true}
              onClose={() => setEditingStepId(null)}
              onUpdate={handleStepUpdateFromPanel}
              onDelete={(stepId) => {
                handleRemoveStep(stepId);
                setEditingStepId(null);
              }}
              onDuplicate={(step) => {
                handleDuplicateStep(step);
                setEditingStepId(null);
              }}
              onAddBrainItemReference={handleAddBrainItemReference}
              onRemoveBrainItemReference={handleRemoveBrainItemReference}
              onAddStep={handleAddStep}
              onRemoveStep={handleRemoveStep}
              availableSteps={formData.steps}
            />
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {topLevelModals}
      {/* Modal Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[10001] flex items-center justify-center overflow-y-auto p-4">
        <motion.div
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="absolute inset-0"
          onClick={handleCloseAttempt}
        />

        {/* Modal Container */}
        <motion.div 
          className="bg-white rounded-xl shadow-xl w-[90vw] h-[90vh] flex flex-col overflow-hidden relative z-10"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-5 sm:px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-20">
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                <GitBranch className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-medium text-gray-900">
                  {isNew ? 'Create Workflow' : 'Edit Workflow'}
                </h2>
                <p className="text-xs text-gray-500">
                  Build multi-step decision tree conversations
                </p>
              </div>
            </div>

            <button 
              onClick={handleCloseAttempt}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Main Content (Scrollable) */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Basic Settings */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700 flex items-center">
                  <Info className="h-4 w-4 text-gray-500 mr-1.5" />
                  Basic Information
                </h3>

                {/* Name */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-700">
                    Workflow Name <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter workflow name"
                    className="text-sm"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-700">
                    Description
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Describe what this workflow does"
                    className="text-sm"
                    rows={3}
                  />
                </div>

                {/* Category Selection */}
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-700">
                    Category <span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <Select
                    value={formData.category_id}
                    onValueChange={handleCategoryChange}
                  >
                    <SelectTrigger className="h-10 text-sm border-gray-200">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent className="z-[10005]">
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id} className="text-sm">
                          {category.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="new" className="text-sm text-gray-500">
                        <Plus className="h-4 w-4 mr-1" /> New Category
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Qualifiers Section */}
              {accountId && (
                <div className="space-y-4 pt-4 border-t border-gray-100">
                   <BrainSettingConditions
                    settingId={formData.id}
                    accountId={accountId}
                    initialRequirements={requirements}
                    onRequirementsChange={(newReqs) => {
                      setRequirements(newReqs);
                      setHasUnsavedChanges(true);
                    }}
                  />
                </div>
              )}

              {/* Triggers */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-700 flex items-center">
                  <Tag className="h-4 w-4 text-gray-500 mr-1.5" />
                  Trigger Phrases
                </h3>

                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      value={triggerInput}
                      onChange={(e) => setTriggerInput(e.target.value)}
                      placeholder="Add a trigger phrase"
                      className="text-sm"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddTrigger()}
                    />
                    <Button
                      type="button"
                      onClick={handleAddTrigger}
                      className="px-3 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {formData.triggers.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.triggers.map((trigger, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                        >
                          {trigger}
                          <button
                            type="button"
                            onClick={() => handleRemoveTrigger(index)}
                            className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-green-600 hover:bg-green-200 focus:outline-none"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Workflow Steps - React Flow */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-gray-700 flex items-center">
                    <GitBranch className="h-4 w-4 text-gray-500 mr-1.5" />
                    Workflow Design
                  </h3>
                  <div className="text-xs text-gray-500">
                    Click on steps to edit • {formData.steps.length} step{formData.steps.length !== 1 ? 's' : ''}
                  </div>
                </div>

                <div className="w-full h-96 border border-gray-200 rounded-lg overflow-hidden">
                  <WorkflowFlow
                    steps={formData.steps || []}
                    onStepEdit={handleStepEdit}
                    onAddStep={handleAddStep}
                    onDeleteStep={handleRemoveStep}
                    className="w-full h-full"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end space-x-3">
            <Button
              type="button"
              onClick={handleCloseAttempt}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <FloppyDisk className="h-4 w-4 mr-2" />
                  {isNew ? 'Create Workflow' : 'Update Workflow'}
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Step Edit Panel */}
      {editingStepId && currentEditingStep && (
        <StepEditPanel
          step={currentEditingStep}
          isOpen={true}
          onClose={() => setEditingStepId(null)}
          onUpdate={handleStepUpdateFromPanel}
          onDelete={(stepId) => {
            handleRemoveStep(stepId);
            setEditingStepId(null);
          }}
          onDuplicate={(step) => {
            handleDuplicateStep(step);
            setEditingStepId(null);
          }}
          onAddBrainItemReference={handleAddBrainItemReference}
          onRemoveBrainItemReference={handleRemoveBrainItemReference}
          onAddStep={handleAddStep}
          onRemoveStep={handleRemoveStep}
          availableSteps={formData.steps}
        />
      )}

      {/* Unsaved Changes Modal */}
      <ConfirmModal
        isOpen={isUnsavedChangesModalOpen}
        onClose={() => setIsUnsavedChangesModalOpen(false)}
        onConfirm={handleCloseConfirmed}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Close Without Saving"
        cancelText="Keep Editing"
        variant="danger"
      />
    </>
  );
};

export default WorkflowModal; 